import "./EventList.css";

import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { ErrorBoundary } from "../../../errors/ErrorBoundary";
import Modal, { ModalSize } from "../../../misc/components/modal";
import {
    CloseDisputedListDialogAction,
    CloseEventListDialogAction,
    JumpToTimePointAction,
    RemovePeriodEventAction,
    RemovePointEventAction,
    SetDuplicateVerifiedAction
} from "../actions";
import { AnalyseState } from "../interfaces/state";
import { PeaksInstance, PointOptions, SegmentOptions } from "peaks.js";
import { DisputedListTable } from "./DisputedListTable";
import { EventTotals } from "./EventTotals";
import { Query } from '../../../misc/components/queryTable/Query';
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { Form } from "informedv4";

export interface DisputedListProps {
    isVisible: boolean;
    pointEvents: PointOptions[];
    periodEvents: SegmentOptions[];
    selectedSegment: number;
    dispatch?: Dispatch<AnalyseState>;
    selectedWaveform: string;
    isViewOnly: boolean;
    primaryAnalysisSet: string;
    peaksJs: PeaksInstance;
    analyst: string;
    coughAnalysisSetStatus: string;
}

export const DisputedList = (props: DisputedListProps) => {
    const [periodTableQuery, setPeriodTableQuery] = React.useState<Query<any>>();
    const [pointTableQuery, setPointTableQuery] = React.useState<Query<any>>();
    const legacyClient = useWebPortalLegacyClient();
    const [forceRerender, setForceRerender] = React.useState(0);

    const refresh = () => {
        setForceRerender(prev => prev + 1);
    };

    if (props.isVisible) {

        return (
            <Form validateOn="submit">
                <Modal
                    show={true}
                    size={ModalSize.Large}
                    header="Disputed Event List"
                    closeModalHandler={() =>
                        props.dispatch(CloseDisputedListDialogAction.create())
                    }>
                    <div className="modal-body">
                        <div className="row">
                            <div className="col-md-12">
                                <EventTotals
                                    pointEvents={props.pointEvents.filter(x => x.disputed === true)}
                                    periodEvents={null}
                                    isDisputedView={true}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-12">
                                <h3 className="text-primary">
                                    Disputed Point Events
                                </h3>
                                <ErrorBoundary>
                                    <DisputedListTable
                                        dispatch={props.dispatch}
                                        key="PointsEventsTable"
                                        startTimeColumnName="Waveform Time"
                                        timestampColumn="timestamp"
                                        withEndTime={false}
                                        events={props.pointEvents.filter(x => x.disputed === true)}
                                        renderRawTime
                                        onJumpToEventClick={timepoint => {
                                            props.dispatch(
                                                JumpToTimePointAction.create(
                                                    timepoint
                                                )
                                            );
                                            props.dispatch(
                                                CloseDisputedListDialogAction.create()
                                            );
                                        }}
                                        onDeleteEventClick={id =>
                                            props.dispatch(
                                                RemovePointEventAction.create(
                                                    legacyClient,
                                                    id
                                                )
                                            )
                                        }
                                        onConfirmDuplicateThresholdEventClick={async id => {
                                            props.dispatch(SetDuplicateVerifiedAction.create(id));
                                        }}
                                        query={pointTableQuery}
                                        selectedSegment={props.selectedSegment}
                                        selectedWaveform={props.selectedWaveform}
                                        isViewOnly={props.isViewOnly}
                                        primaryAnalysisSet={props.primaryAnalysisSet}
                                        peaksJs={props.peaksJs}
                                        coughAnalysisSetStatus={props.coughAnalysisSetStatus}
                                        analyst={props.analyst}
                                        refresh={refresh}
                                    />
                                </ErrorBoundary>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button
                            className="btn btn-primary"
                            onClick={() =>
                                props.dispatch(
                                    CloseDisputedListDialogAction.create()
                                )
                            }>
                            Close
                        </button>
                    </div>
                </Modal>
            </Form>
        );
    } else {
        return null;
    }
}

function mapStateToProps(state: AnalyseState) {
    return {
        isVisible: state.core.isDisputedListOpen,
        pointEvents: [...state.core.peaksJs.points.getPoints()],
        periodEvents: [...state.core.peaksJs.segments.getSegments()],
        selectedSegment: state.core.selectedSegment,
        selectedWaveform: state.core.selectedWaveform,
        isViewOnly: state.core.isViewOnly,
        primaryAnalysisSet: state.analysisSet.primaryAnalystSetId,
        peaksJs: state.core.peaksJs,
        coughAnalysisSetStatus: state.core.coughAnalysisSet.status,
        analyst: state.core.coughAnalysisSet.analyst

    };
}

export default connect<DisputedListProps, any>(mapStateToProps)(DisputedList);
